import React, { useEffect, useState, useRef } from 'react';
import { FaTimes, FaSearch } from 'react-icons/fa';

function SearchBar({
  searchQuery,
  setSearchQuery,
  suggestions,
  fetchSuggestions,
  errorMessage,
  handleSearch,
  resetSearch
}) {
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Debounce suggestions
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        fetchSuggestions(searchQuery);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchQuery, fetchSuggestions]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      setShowSuggestions(false);
      handleSearch();
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    } else if (event.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleClear = () => {
    resetSearch();
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const title = suggestion.title || suggestion.name;
    setSearchQuery(title);
    setShowSuggestions(false);
    handleSearch(title);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="search-bar-container" ref={containerRef}>
      <div className="search-input-box">
        <div className="search-icon-left">
          <FaSearch />
        </div>
        <input
          ref={searchInputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => searchQuery.trim().length >= 2 && setShowSuggestions(true)}
          placeholder="Search movies, TV shows, directors..."
          autoComplete="off"
          aria-label="Search movies and TV shows"
        />
        {searchQuery && (
          <button
            type="button"
            className="clear-search-btn"
            onClick={handleClear}
            aria-label="Clear search query"
          >
            <FaTimes />
          </button>
        )}
        <button
          type="button"
          className="search-submit-btn"
          onClick={() => handleSearch()}
          aria-label="Submit search"
        >
          <FaSearch />
        </button>

        {showSuggestions && suggestions && suggestions.length > 0 && (
          <div className="suggestions-dropdown">
            {suggestions.map((suggestion) => {
              const title = suggestion.title || suggestion.name;
              const date = suggestion.release_date || suggestion.first_air_date;
              const year = date ? `(${date.slice(0, 4)})` : '';
              const isMovie = suggestion.media_type === 'movie';
              const thumbUrl = suggestion.poster_path
                ? `https://image.tmdb.org/t/p/w92${suggestion.poster_path}`
                : null;

              return (
                <div
                  key={suggestion.id}
                  className="suggestion-item"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <div className="suggestion-left">
                    {thumbUrl ? (
                      <img
                        src={thumbUrl}
                        alt=""
                        className="suggestion-thumb"
                        loading="lazy"
                      />
                    ) : (
                      <div className="suggestion-thumb" />
                    )}
                    <span className="suggestion-title">
                      {title} {year && <span className="suggestion-year">{year}</span>}
                    </span>
                  </div>
                  <span className={`suggestion-type-badge ${isMovie ? 'movie' : 'tv'}`}>
                    {isMovie ? 'Movie' : 'TV'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchBar;